/**
 * The chain view: a semantic path between two entities, hop by hop.
 *
 * relation-extraction design D7: a chain is discovered at query time and durable only
 * once pinned into an artifact. Each hop opens its own sentence in the shared evidence
 * panel — the path tokens marked within the wider covering quote via the existing
 * `EvidenceRef` context — never the two endpoints alone, because A -> C with the steps
 * hidden is exactly the "inference passing as a quote" failure this whole feature
 * exists to avoid.
 *
 * A cluster-typed hop's relation label carries its claim marking (design D5): a name
 * is the naming pass's own summary of exemplars, not a fact, and it reads that way
 * here. An unreviewed hop (`proposed`) is marked unreviewed, matching every other
 * review-carrying surface in this codebase.
 */

import { useCallback, useMemo, useState } from "react";
import type { RelationEdge, TypedNode } from "../lib/relationsApi";
import { relationsApi } from "../lib/relationsApi";
import { useEvidence } from "../state/evidence";
import { useGatedQuery } from "../state/renderGate";

function reviewLabel(state: RelationEdge["review_state"]): string {
  return state === "proposed" ? "⚠ proposed — unreviewed" : state;
}

function hopLabel(hop: RelationEdge, nodesByCluster: Map<string, TypedNode>): string {
  const subject = nodesByCluster.get(hop.subject_cluster)?.display_value ?? hop.subject_cluster;
  const object = nodesByCluster.get(hop.object_cluster)?.display_value ?? hop.object_cluster;
  // trigger-relations design D3: direction is never guessed. `case_marked` is the only
  // state that has earned a directional arrow — `surface_order` (and a dependency-path
  // or declared relation, which carry no `direction` at all) render undirected, the
  // same em-dash chrome this label always used.
  const connector = hop.direction === "case_marked" ? "→" : "—";
  return `${subject} ${connector} ${hop.relation_type ?? "(untyped)"} ${connector} ${object}`;
}

// trigger-relations design D6: the method badge. `null` (a declared relation or a
// dependency-path instance, neither of which writes `attrs.method` yet) renders no
// badge at all — absence is not itself a claim about method.
function methodLabel(method: string | null): string | null {
  if (method === "trigger_window") return "trigger";
  if (method === "dependency_path") return "parsed";
  return null;
}

export function Chains({ datasetId }: { datasetId: string }) {
  const { select } = useEvidence();
  const [source, setSource] = useState("");
  const [target, setTarget] = useState("");
  const [maxHops, setMaxHops] = useState(2);
  const [pinError, setPinError] = useState<string | null>(null);
  const [pinned, setPinned] = useState<string | null>(null);

  const graphQuery = useMemo(() => ({ datasetId }), [datasetId]);
  const fetchGraph = useCallback(
    (active: typeof graphQuery) => relationsApi.typedGraph(active.datasetId),
    [],
  );
  const { rows: graph, error: graphError } = useGatedQuery(graphQuery, fetchGraph);

  const nodesByCluster = useMemo(() => {
    const map = new Map<string, TypedNode>();
    for (const node of graph?.nodes ?? []) map.set(node.cluster_id, node);
    return map;
  }, [graph]);

  const chainsQuery = useMemo(
    () => ({ datasetId, source, target, maxHops }),
    [datasetId, source, target, maxHops],
  );
  const fetchChains = useCallback((active: typeof chainsQuery) => {
    if (!active.source || !active.target) return Promise.resolve(null);
    return relationsApi.chains(active.datasetId, active.source, active.target, active.maxHops);
  }, []);
  const { rows: result, error: chainsError } = useGatedQuery(chainsQuery, fetchChains);

  function openHop(hop: RelationEdge) {
    if (!hop.block_id || hop.start_cp === null || hop.end_cp === null || hop.quote === null) return;
    // Path or trigger tokens marked within the covering quote (design D2): each offset
    // is block-relative, exactly like the covering span, so slicing the covering quote
    // by the relative offset reproduces the exact substring the evidence panel's own
    // quote-integrity check expects. A trigger-produced hop has no `path_offsets` (the
    // dependency-path producer's own field); a dependency-path hop has no
    // `trigger_offsets` — at most one of the two is ever non-empty for a given hop.
    const offsets = hop.trigger_offsets.length > 0 ? hop.trigger_offsets : hop.path_offsets;
    const highlightSpans = offsets.map(([start, end]) => ({
      start_cp: start,
      end_cp: end,
      quote: hop.quote!.slice(start - hop.start_cp!, end - hop.start_cp!),
    }));
    select({
      blockId: hop.block_id,
      spans:
        highlightSpans.length > 0
          ? highlightSpans
          : [{ start_cp: hop.start_cp, end_cp: hop.end_cp, quote: hop.quote }],
      label: hopLabel(hop, nodesByCluster),
      origin: "chain",
    });
  }

  async function pinChain(chainIndex: number) {
    setPinError(null);
    setPinned(null);
    try {
      const artifact = await fetch(`/api/datasets/${datasetId}/artifacts`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          title: "Pinned chain",
          prompt: "n/a",
          max_llm_calls: 1,
          max_rows: 1,
        }),
      });
      const artifactBody = (await artifact.json()) as { artifact_id?: string; detail?: string };
      if (!artifact.ok || !artifactBody.artifact_id) {
        throw new Error(artifactBody.detail ?? "could not create an artifact to pin into");
      }
      const pin = await relationsApi.pinChain(datasetId, artifactBody.artifact_id, {
        source,
        target,
        max_hops: maxHops,
        chain_index: chainIndex,
      });
      setPinned(pin.version_id);
    } catch (cause) {
      setPinError(cause instanceof Error ? cause.message : String(cause));
    }
  }

  const error = graphError ?? chainsError ?? pinError;

  return (
    <div className="page">
      <h1>Chains</h1>
      <p className="eyebrow">
        A semantic chain between two entities — induced from the corpus, never chosen
        in advance. Every hop opens its own sentence; select one below.
      </p>

      <div className="row">
        <select
          aria-label="Source identity"
          value={source}
          onChange={(e) => setSource(e.target.value)}
          data-testid="chain-source"
        >
          <option value="">Choose a source…</option>
          {graph?.nodes.map((n) => (
            <option key={n.cluster_id} value={n.cluster_id}>
              {n.display_value ?? n.cluster_id}
            </option>
          ))}
        </select>
        <select
          aria-label="Target identity"
          value={target}
          onChange={(e) => setTarget(e.target.value)}
          data-testid="chain-target"
        >
          <option value="">Choose a target…</option>
          {graph?.nodes.map((n) => (
            <option key={n.cluster_id} value={n.cluster_id}>
              {n.display_value ?? n.cluster_id}
            </option>
          ))}
        </select>
        <label>
          Max hops{" "}
          <input
            type="number"
            min={1}
            max={6}
            value={maxHops}
            onChange={(e) => setMaxHops(Number(e.target.value) || 2)}
            data-testid="chain-max-hops"
          />
        </label>
      </div>

      {error && (
        <p data-testid="chains-error" style={{ color: "var(--danger)" }}>
          {error}
        </p>
      )}

      {pinned && (
        <p data-testid="chain-pinned" style={{ color: "var(--ink-faint)" }}>
          Pinned as artifact version {pinned}.
        </p>
      )}

      <ol className="hits" data-testid="chain-list">
        {result?.chains.map((chain, chainIndex) => (
          <li key={chainIndex}>
            <div className="hit" data-testid="chain">
              <span className="eyebrow">
                {chain.hops.length} hop{chain.hops.length === 1 ? "" : "s"}
              </span>
              <ol className="chain-steps" data-testid="chain-hop-list">
                {chain.hops.map((hop) => (
                  <li key={hop.relation_id}>
                    <button
                      className="hit"
                      data-testid="chain-hop"
                      onClick={() => openHop(hop)}
                    >
                      <span className="hit-text mono">{hopLabel(hop, nodesByCluster)}</span>
                      <span
                        data-testid="chain-hop-type"
                        className={hop.is_claim ? "claim" : "eyebrow"}
                      >
                        {hop.is_claim ? `claim · ${hop.relation_type}` : hop.relation_type}
                      </span>
                      {methodLabel(hop.method) && (
                        <span data-testid="chain-hop-method" className="eyebrow">
                          {methodLabel(hop.method)}
                        </span>
                      )}
                      <span
                        data-testid="chain-hop-state"
                        className={hop.review_state === "proposed" ? "gap" : "eyebrow"}
                      >
                        {reviewLabel(hop.review_state)}
                      </span>
                    </button>
                  </li>
                ))}
              </ol>
              <button
                data-testid="chain-pin"
                onClick={() => void pinChain(chainIndex)}
              >
                Pin this chain
              </button>
            </div>
          </li>
        ))}
      </ol>

      {result && result.chains.length === 0 && (
        <p data-testid="chains-empty" style={{ color: "var(--ink-faint)" }}>
          No chain found within {maxHops} hop(s).
        </p>
      )}
    </div>
  );
}
